/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { CustomerAddressesModel } from '../models';
import { AddressResponse, UserAddressesProps } from '@/account/types';
import { convertKeysCase } from '@/account/lib/convertCase';

const expandArraysInObject = (inputObject: UserAddressesProps) => {
  const flattenedAttributes: Record<string, unknown> = {};

  for (const key in inputObject) {
    const element = inputObject[key as keyof UserAddressesProps];

    if (!Array.isArray(element) || element.length === 0) continue;

    if (key === 'custom_attributesV2') {
      element.forEach((item) => {
        if (typeof item === 'object' && 'value' in item) {
          flattenedAttributes[item?.code] = item?.value;
        }
      });
    } else if (element.length > 1) {
      element.forEach((value: unknown, index: number) => {
        index === 0
          ? (flattenedAttributes[key] = value)
          : (flattenedAttributes[`${key}_multiline_${index + 1}`] = value);
      });
    } else {
      flattenedAttributes[key] = element[0] as Record<string, string>;
    }
  }

  return flattenedAttributes;
};
const transformFullName = (address: UserAddressesProps) => {
  return {
    prefix: address?.prefix ?? '',
    suffix: address?.suffix ?? '',
    firstname: address?.firstname ?? '',
    lastname: address?.lastname ?? '',
    middlename: address?.middlename ?? '',
  };
};

const transformIds = (address: UserAddressesProps) => {
  return {
    id: address?.id ?? '',
    vat_id: address?.vat_id ?? '',
    postcode: address?.postcode ?? '',
    country_code: address?.country_code ?? '',
    uid: address?.uid ?? '',
  };
};

const transformContacts = (address: UserAddressesProps) => {
  return {
    company: address?.company ?? '',
    telephone: address?.telephone ?? '',
    fax: address?.fax ?? '',
  };
};

// Function to transform a single address
export const transformSingleAddress = (
  addressData: UserAddressesProps
): CustomerAddressesModel => {
  const result = convertKeysCase(
    {
      ...transformFullName(addressData),
      ...transformIds(addressData),
      ...transformContacts(addressData),
      city: addressData?.city ?? '',
      region: {
        region: addressData?.region?.region ?? '',
        region_code: addressData?.region?.region_code ?? '',
        region_id: addressData?.region?.region_id ?? '',
      },
      default_shipping: addressData?.default_shipping || false,
      default_billing: addressData?.default_billing || false,
      ...expandArraysInObject(addressData),
    },
    'camelCase',
    {}
  );

  return result;
};

// Function to transform multiple addresses from a response
export const transformMultipleAddresses = (
  response: AddressResponse
): CustomerAddressesModel[] | [] => {
  const addresses: UserAddressesProps[] =
    response?.data?.customer?.addresses || [];

  if (!addresses.length) return [];

  const result = addresses
    .map(transformSingleAddress)
    .sort(
      (a, b) =>
        (Number(b.defaultBilling) || Number(b.defaultShipping)) -
        (Number(a.defaultBilling) || Number(a.defaultShipping))
    );

  return result;
};
